import React, { createContext, useContext, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, CreditCard, X, ArrowRight, Plug, Shield, Building2, Key } from 'lucide-react';
import { useSelector, shallowEqual } from 'react-redux';
import { useSubscriptionData } from '../../../../hooks/useSubscriptionStatus';
import './subscription.scss';

// ── Feature metadata ──────────────────────────────────────────────────────────

const FEATURE_META = {
  has_integrations: {
    Icon: Plug,
    title: 'Third-party Integrations',
    description: 'Connect HR tools, identity providers, and directory services to automate staff and access management.',
    cta: 'Unlock Integrations',
    accentColor: '#5341C4',
    accentBg: '#F0EEFF',
  },
  ml_risk_scoring: {
    Icon: Shield,
    title: 'AI Safety Intelligence',
    description: 'AI-powered risk scoring assigns every visitor a threat level based on behavioural and network signals.',
    cta: 'Unlock AI Safety',
    accentColor: '#DC2626',
    accentBg: '#FEF2F2',
  },
  max_spaces: {
    Icon: Building2,
    title: 'Additional Locations',
    description: 'Your current plan limits the number of locations you can manage. Upgrade to add more sites.',
    cta: 'Upgrade Plan',
    accentColor: '#2563EB',
    accentBg: '#EFF6FF',
  },
  api_access: {
    Icon: Key,
    title: 'Developer API Access',
    description: 'Build custom integrations using API keys and webhook endpoints for your security infrastructure.',
    cta: 'Unlock API Access',
    accentColor: '#16A34A',
    accentBg: '#F0FDF4',
  },
};

// ── Nudge context ─────────────────────────────────────────────────────────────

const NudgeContext = createContext(null);

export function UpgradeNudgeProvider({ children }) {
  const [featureKey, setFeatureKey] = useState(null);
  const show = useCallback((key) => setFeatureKey(key), []);
  const hide = useCallback(() => setFeatureKey(null), []);

  return (
    <NudgeContext.Provider value={{ show }}>
      {children}
      {featureKey && <UpgradeNudgeModal featureKey={featureKey} onClose={hide} />}
    </NudgeContext.Provider>
  );
}

export function useNudge() {
  const ctx = useContext(NudgeContext);
  if (!ctx) throw new Error('useNudge must be inside UpgradeNudgeProvider');
  return ctx;
}

// ── Modal ─────────────────────────────────────────────────────────────────────

function UpgradeNudgeModal({ featureKey, onClose }) {
  const navigate = useNavigate();
  const billingPlans = useSelector((state) => state.dashboard.billingPlans, shallowEqual);

  const meta = FEATURE_META[featureKey] || {
    Icon: Lock,
    title: 'Premium Feature',
    description: 'This feature is available on a higher plan.',
    cta: 'View Plans',
    accentColor: '#5341C4',
    accentBg: '#F0EEFF',
  };

  const { Icon, title, description, cta, accentColor, accentBg } = meta;

  const upgradePlan = Array.isArray(billingPlans)
    ? billingPlans.find((p) => p.features?.[featureKey] === true)
    : null;

  function handleUpgrade() {
    onClose();
    navigate('/settings?tab=billing');
  }

  return (
    <div className="nudge-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="nudge-modal" role="dialog" aria-modal="true">
        <button className="nudge-close" onClick={onClose} aria-label="Close">
          <X size={16} />
        </button>

        <div className="nudge-icon-wrap">
          <div className="nudge-icon" style={{ background: accentBg, color: accentColor }}>
            <Icon size={24} />
          </div>
          <div className="nudge-icon-lock">
            <Lock size={11} />
          </div>
        </div>

        <div className="nudge-body">
          <p className="nudge-label">Upgrade required</p>
          <h3 className="nudge-title">{title}</h3>
          <p className="nudge-desc">{description}</p>

          {upgradePlan && (
            <div className="nudge-plan-hint">
              Available on the <strong>{upgradePlan.name}</strong> plan and above
            </div>
          )}
        </div>

        <div className="nudge-actions">
          <button className="nudge-btn nudge-btn--primary" onClick={handleUpgrade}>
            <CreditCard size={15} />
            {cta}
            <ArrowRight size={14} />
          </button>
          <button className="nudge-btn nudge-btn--ghost" onClick={onClose}>
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Component gate ────────────────────────────────────────────────────────────

export function FeatureGate({ children, feature, fallback }) {
  const navigate = useNavigate();
  const { hasAccess, canUseFeature } = useSubscriptionData();

  const allowed = feature ? canUseFeature(feature) : hasAccess;
  if (allowed) return <>{children}</>;
  if (fallback) return <>{fallback}</>;

  return (
    <div className="feature-gate">
      <Lock size={18} />
      <span>This feature requires an active subscription.</span>
      <button
        className="feature-gate__btn"
        onClick={() => navigate('/settings?tab=billing&action=subscribe')}
      >
        <CreditCard size={14} />
        View Plans
      </button>
    </div>
  );
}

// ── Hook for imperative use ───────────────────────────────────────────────────
//
// Usage:
//   const { guardAction } = useFeatureGate();
//   <button onClick={() => guardAction(() => doSomething(), 'has_integrations')}>Connect</button>
//
export function useFeatureGate() {
  const { hasAccess, canUseFeature, needsPayment, plan } = useSubscriptionData();
  const { show: showNudge } = useNudge();

  const guardAction = useCallback(
    (fn, feature) => {
      const allowed = feature ? canUseFeature(feature) : hasAccess;
      if (allowed) {
        fn();
      } else {
        showNudge(feature || '__subscription__');
      }
    },
    [canUseFeature, hasAccess, showNudge],
  );

  // Numeric limit guard — e.g. max_spaces: 2
  const guardLimit = useCallback(
    (featureKey, currentCount, fn) => {
      const limit = plan?.features?.[featureKey];
      if (limit != null && typeof limit === 'number' && currentCount >= limit) {
        showNudge(featureKey);
      } else {
        fn();
      }
    },
    [plan, showNudge],
  );

  return { guardAction, guardLimit, hasAccess, canUseFeature, needsPayment, plan };
}
